import React, { useEffect, useState } from "react";
import ReactTable from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../../shared/notify";

import Modal from "../../components/modal";
import TaskNav from "../../components/task-nav/task-nav";

import { Input } from "../../components/form-inputs/inputs";
import { getProduct, getProducts, searchUpc, updateRelationships } from "./lib/data";
import { useInput } from "../../shared/hooks/form-fields";
import { KEYCODE } from "../../shared/constants";
import { columns, matchColumns } from "./lib/constants";
import { useParams } from "react-router-dom";

const notifyNoMatch = () => {
    notify.warning("No Match found!");
};

const verifyMatch = (upc, modifier, match) => {
    if (upc !== match.upc) {
        return false;
    }

    if ((modifier || match.productModifier) && modifier.toLowerCase() !== match.productModifier.toLowerCase()) {
        return false;
    }

    return true;
};

const LinkingManagerIndex = () => {
    const params = useParams();
    const productKey = params.id;
    const [currentProduct, setCurrentProduct] = useState({
        key: "",
        shipmentKey: "",
        title: "",
    });
    const [childList, setChildList] = useState([]);
    const [parentList, setParentList] = useState([]);
    const [modal, setModal] = useState({
        matches: [],
        showParent: false,
        showChild: false,
    });

    const childUpc = useInput("");
    const childModifier = useInput("");
    const parentUpc = useInput("");
    const parentModifier = useInput("");

    useEffect(() => {
        async function fetchData() {
            const product = await getProduct(productKey);

            if (product) {
                const fullUpc = product.modifier ? `${product.upc}-${product.modifier}` : product.upc;
                const upcWithPackageLevel = product.packageLevelName ? `${fullUpc} (${product.packageLevelName})` : fullUpc;
                setCurrentProduct({
                    ...product,
                    title: upcWithPackageLevel,
                });
            }

            const parentProducts = await getProducts(product.parentProductKeys);
            setParentList(parentProducts);

            const childProducts = await getProducts(product.childProductKeys);
            setChildList(childProducts);
        }

        fetchData();
    }, []);
    const findUpc = async (upc, modifier, relationship) => {
        if (!upc || upc.length < 3) {
            notify.error("UPC requires 3 characters to search");

            return;
        }

        const upcResults = await searchUpc(upc);

        if (!upcResults) {
            return;
        }

        if (upcResults.length <= 0) {
            notifyNoMatch();

            return;
        }

        if (upcResults.length === 1) {
            if (!verifyMatch(upc, modifier, upcResults[0])) {
                notify.warning("No records match");

                return;
            }

            if (!checkDuplicate(upcResults[0].shipmentProductKey)) {
                return;
            }

            if (relationship === "parent") {
                addParent(upcResults[0]);
                parentUpc.reset();
            } else {
                addChild(upcResults[0]);
                childUpc.reset();
            }

            return;
        }

        setModal({
            matches: [...upcResults],
            showParent: relationship === "parent",
            showChild: relationship === "child",
        });

        parentUpc.reset();
        childUpc.reset();
    };
    const searchParent = () => {
        findUpc(parentUpc.value, parentModifier.value, "parent");
    };
    const searchChild = () => {
        findUpc(childUpc.value, childModifier.value, "child");
    };
    const handleParentEnter = (e) => {
        if (e.which === KEYCODE.RETURN || e.keyCode === KEYCODE.RETURN) {
            searchParent();
        }
    };
    const handleChildEnter = (e) => {
        if (e.which === KEYCODE.RETURN || e.keyCode === KEYCODE.RETURN) {
            searchChild();
        }
    };

    const closeModal = () => {
        setModal({
            matches: [],
            showParent: false,
            showChild: false,
        });
    };

    const checkDuplicate = (selectedKey) => {
        if (childList.map((i) => i.shipmentProductKey).includes(selectedKey) || parentList.map((i) => i.shipmentProductKey).includes(selectedKey)) {
            notify.error("Upc already exists as parent or child");

            return false;
        }

        if (selectedKey === currentProduct.key) {
            notify.error("Upc can not be added to itself");

            return false;
        }

        return true;
    };

    const addParent = (upc) => {
        if (!checkDuplicate(upc.shipmentProductKey)) {
            return;
        }

        const newUpc = {
            ...upc,
            key: upc.shipmentProductKey,
        };
        setParentList([...parentList, newUpc]);
    };
    const removeParent = (key) => {
        const newParentList = parentList.filter((i) => i.key !== key);
        setParentList([...newParentList]);
    };

    const addChild = (upc) => {
        if (!checkDuplicate(upc.shipmentProductKey)) {
            return;
        }

        const newUpc = {
            ...upc,
            key: upc.shipmentProductKey,
        };
        setChildList([...childList, newUpc]);
    };
    const removeChild = (key) => {
        const newChildList = childList.filter((i) => i.key !== key);
        setChildList([...newChildList]);
    };

    const parentRowProps = (state, rowInfo, column, instance) => {
        return {
            onClick: (e) => {
                addParent(rowInfo.original);
                closeModal();
            },
            style: {
                cursor: "pointer",
            },
        };
    };

    const childRowProps = (state, rowInfo, column, instance) => {
        return {
            onClick: () => {
                addChild(rowInfo.original);
                closeModal();
            },
            style: {
                cursor: "pointer",
            },
        };
    };

    const saveRelationships = async () => {
        if (!currentProduct.shipmentKey) {
            notify.error("Save not available yet.");

            return;
        }

        const parentProductKeys = parentList.map((i) => i.key);
        const childProductKeys = childList.map((i) => i.key);

        const response = await updateRelationships(currentProduct.key, {
            childProductKeys,
            parentProductKeys,
        });

        if (!response) {
            return;
        }

        notify.success("Saved!");
    };

    return (
        <div>
            <TaskNav>
                <div className="button-group">
                    <h1>Linking Manager - {currentProduct.title}</h1>
                    <button className="button -primary" onClick={saveRelationships}>
                        Save
                    </button>
                </div>
            </TaskNav>
            <div className="row">
                <div className="column -span6">
                    <h3>Parents</h3>
                    <div className="input-group -no-margin">
                        <Input name="parentUpc" placeholder="Upc" {...parentUpc.bind} onKeyUp={handleParentEnter} />
                        <Input name="parentModifier" className="-small" placeholder="Modifier" {...parentModifier.bind} onKeyUp={handleParentEnter} />
                        <button className="button -primary -margin-left" onClick={searchParent}>
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                    </div>
                    <br />
                    <ReactTable data={parentList} columns={matchColumns(removeParent)} minRows="10" />
                </div>
                <div className="column -span6">
                    <h3>Children</h3>
                    <div className="input-group -no-margin">
                        <Input name="childUpc" placeholder="Link Upc" {...childUpc.bind} onKeyUp={handleChildEnter} />
                        <Input name="childModifier" className="-small" placeholder="Modifier" {...childModifier.bind} onKeyUp={handleParentEnter} />
                        <button className="button -primary -margin-left" onClick={searchChild}>
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                    </div>
                    <br />
                    <ReactTable data={childList} columns={matchColumns(removeChild)} minRows="10" />
                </div>
            </div>
            <Modal show={modal.showParent} onClose={closeModal}>
                <h3>Multiple Matches</h3>
                <p>Please select a match</p>
                <ReactTable data={modal.matches} columns={columns} minRows="5" showPagination={false} getTrProps={parentRowProps} />
            </Modal>
            <Modal show={modal.showChild} onClose={closeModal}>
                <h3>Multiple Matches</h3>
                <p>Please select a match</p>
                <ReactTable data={modal.matches} columns={columns} minRows="5" showPagination={false} getTrProps={childRowProps} />
            </Modal>
        </div>
    );
};

export default LinkingManagerIndex;
